import { FC } from 'react';
import { Box, Button } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import {
  DataGridPro,
  GridPagination,
  GridSortDirection,
} from '@mui/x-data-grid-pro';
import { ConfirmationModal, TableNoResultPlaceholder } from '@components';
import { useStableGridModels } from '@common/hooks';
import {
  PinnedColumnMenu,
  ColumnDensityFilter,
  TableColumnHeightMapper,
} from 'components/TablePreferences';
import { useReportFoldersDataGridTable } from '../hooks/useReportFoldersDataGridTable';
import { ReportFoldersTableEntry } from '../hooks/useReportFoldersTable';
import { ReportFoldersTableFilters } from '../hooks/useReportFoldersTable';
import { ReportFoldersTableFilters as ReportFoldersFiltersComponent } from './ReportFoldersTableFilters';
import { useNavigate } from 'react-router-dom';

interface ReportFoldersDataGridProps {
  rows: ReportFoldersTableEntry[];
  totalCount: number;
  loading: boolean;
  filters: ReportFoldersTableFilters;
  setFilters: (filters: ReportFoldersTableFilters) => void;
  handleOpenDeleteModal: (id: string) => void;
  handleMakeReportFolderFavorite: (id: string, isFavorite: boolean) => void;
  makeReportFolderFavoriteIsPending: boolean;
  reportIdToFavorite: string;
}

export const ReportFoldersDataGrid: FC<ReportFoldersDataGridProps> = ({
  rows,
  totalCount,
  loading,
  filters,
  setFilters,
  handleOpenDeleteModal,
  handleMakeReportFolderFavorite,
  makeReportFolderFavoriteIsPending,
  reportIdToFavorite,
}) => {
  const navigate = useNavigate();

  const {
    columns,
    handleChangeOrder,
    onPageModelChange,
    handleResetAll,
    initialSortFilters,
    isResetModalOpen,
    setIsResetModalOpen,
    pinnedColumns,
    handlePinnedColumnsChange,
    handleColumnOrderChange,
    handleColumnWidthChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    density,
    handleDensityChange,
    EditModal,
    apiRef,
  } = useReportFoldersDataGridTable({
    filters,
    setFilters,
    handleOpenDeleteModal,
    handleMakeReportFolderFavorite,
    makeReportFolderFavoriteIsPending,
    reportIdToFavorite,
  });

  const { paginationModel, sortModel, rowCount } = useStableGridModels({
    page: filters.page ?? 1,
    limit: filters.limit ?? 25,
    sort_by: filters.sort_by,
    sort_order: filters.sort_order?.toLowerCase(),
    totalCount,
    loading,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <ReportFoldersFiltersComponent
        endSlot={
          <>
            <ColumnDensityFilter
              handleDensityChange={handleDensityChange}
              selectedValue={density}
            />
          </>
        }
      />

      <DataGridPro
        apiRef={apiRef}
        sx={{
          maxHeight: 'calc(100vh - 440px)',
          '--DataGrid-overlayHeight': 'calc(100vh - 440px)',
          borderRadius: 0,
          boxShadow: 'none',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
          borderBottom: 'none',
          '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex' },
          '& .MuiDataGrid-row': { cursor: 'pointer' },
        }}
        slots={{
          columnMenu: props => (
            <PinnedColumnMenu {...props} pinnedColumns={pinnedColumns} />
          ),
          pagination: props => (
            <Box
              sx={{
                px: 2,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Button
                color='primary'
                sx={{ whiteSpace: 'nowrap' }}
                startIcon={<Refresh color='primary' />}
                onClick={() => setIsResetModalOpen(true)}
              >
                RESET VIEW
              </Button>
              <GridPagination {...props} />
            </Box>
          ),
          noRowsOverlay: () => (
            <Box
              sx={{
                height: '100%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: theme => theme.palette.common.white,
              }}
            >
              <TableNoResultPlaceholder />
            </Box>
          ),
        }}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnWidthChange={handleColumnWidthChange}
        pageSizeOptions={[10, 25, 50, 100]}
        pagination
        paginationModel={paginationModel}
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        loading={loading}
        disableMultipleColumnsSorting
        disableRowSelectionOnClick
        paginationMode='server'
        sortingMode='server'
        disableColumnFilter
        disableColumnSelector
        disableColumnReorder
        sortModel={sortModel}
        onPaginationModelChange={onPageModelChange}
        onSortModelChange={model => {
          if (
            model[0] &&
            (model[0].field !== filters.sort_by ||
              model[0].sort !== filters.sort_order?.toLowerCase())
          ) {
            handleChangeOrder(model[0].field, model[0].sort);
          } else {
            handleChangeOrder(
              initialSortFilters.sort_by,
              initialSortFilters.sort_order as GridSortDirection,
            );
          }
        }}
        getRowHeight={() => TableColumnHeightMapper[density ?? 'comfortable']}
        getRowClassName={params =>
          params.indexRelativeToCurrentPage % 2 !== 0 ? 'row-even' : ''
        }
        onRowClick={params =>
          navigate(`/reporting/reports-folder-preview/${params.row.id}`)
        }
      />

      <ConfirmationModal
        title='Reset table view'
        message='This will restore the default columns, sorting, and filters. Your custom layout and pinned columns will be cleared.'
        closeText='KEEP CURRENT VIEW'
        confirmText='RESET VIEW'
        onConfirm={() => {
          handleResetAll();
          setIsResetModalOpen(false);
        }}
        onClose={() => setIsResetModalOpen(false)}
        isOpen={isResetModalOpen}
        reverseButtonOrder
      />

      {EditModal}
    </Box>
  );
};
